import axiosClient from '@/plugins/axios';
import qs from 'qs';

const ADDRESS_BASE = '/adressen';
const USER_BASE = '/benutzer';
const PRODUCT_BASE = '/produkte';
const KASSE_BASE = '/kasse';
const META_BASE = '/metadata';
const SAVE_BASE = '/dienstleistungen';

export const userClient = {
  /**
   * Loggt einen Benutzer ein.
   *
   * @param name Nutzername (Email-Adresse) des Benutzers.
   * @param password Passwort des Benutzers.
   * @returns Promise
   */
  login: (name, password) => {
    return axiosClient.post(`${USER_BASE}/login`, qs.stringify({name, password}), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },

  /**
   * Loggt den aktuellen Benutzer aus.
   *
   * @returns Promise
   */
  logout: () => {
    return axiosClient.post(`${USER_BASE}/logged-in/logout`);
  },

  /**
   * Holt allgemeine Daten zu dem aktuell angemeldeten Benutzer ab.
   *
   * @returns Promise
   */
  user: () => {
    return axiosClient.get(`${USER_BASE}/logged-in`);
  },

  /**
   * Registriert einen Benutzer
   *
   * @param user Die Registrierungsdaten des neuen Benutzers.
   * @returns Promise
   */
  registriere: user => {
    return axiosClient.post(USER_BASE, user);
  },

  verifiziereEmail: token => {
    return axiosClient.post(`${USER_BASE}/email-verifizierung/${token}`);
  },

  /**
   * Triggert den Passwort-Vergessen Ablauf.
   *
   * @param emailAdresse Die E-Mail-Adresse des Benutzers.
   * @return Promise.
   */
  passwortVergessen: emailAdresse => {
    return axiosClient.post(`${USER_BASE}/passwort-zuruecksetzen`, {
      emailAdresse,
    });
  },

  /**
   * Setzt das Passwort eines Benutzers zurück.
   *
   * @param passwortNeu Das neue Passwort.
   * @param passwortNeuBestaetigung Die Bestätigung des neuen Passwortes.
   * @param autorisierungsCode Eindeutiges Token für die Verifizierung der Person, welche das Passwort zurücksetzen möchte.
   * @returns Promise
   */
  passwortZuruecksetzen: (passwortNeu, passwortNeuBestaetigung, autorisierungsCode) => {
    return axiosClient.patch(`${USER_BASE}/attribute/passwort`, {
      passwortNeu: passwortNeu,
      passwortNeuBestaetigung: passwortNeuBestaetigung,
      autorisierungsCode: autorisierungsCode,
    });
  },

  /**
   * Ändert das Passwort eines angemeldeten Benutzers.
   *
   * @param emailAdresse Die Mail Adresse des Benutzers.
   * @param passwortAlt Das alte Passwort.
   * @param passwortNeu Das neue Passwort.
   * @param passwortNeuBestaetigung Die Bestätrigung es neuen Passwortes.
   * @returns Promise
   */
  passwortAendern: (emailAdresse, passwortAlt, passwortNeu, passwortNeuBestaetigung) => {
    return axiosClient.patch(`${USER_BASE}/logged-in/attribute/passwort`, {
      emailAdresse: emailAdresse,
      passwortAlt: passwortAlt,
      passwortNeu: passwortNeu,
      passwortNeuBestaetigung: passwortNeuBestaetigung,
    });
  },
};

export const addressClient = {
  addressQuery: query => {
    return axiosClient.get(`${ADDRESS_BASE}/adresseQuery`, {
      params: {query},
    });
  },
  address: address => {
    return axiosClient.get(`${ADDRESS_BASE}/adresse`, {
      params: {
        ...address,
      },
    });
  },
  addressPosition: (lat, lng) => {
    return axiosClient.get(`${ADDRESS_BASE}/adressePosition`, {
      params: {lat, lng},
    });
  },
  updateAdresse: () => {
    return axiosClient.patch(`${ADDRESS_BASE}/adresseUebernehmen`);
  },
  clearAddress: () => {
    return axiosClient.delete(`${ADDRESS_BASE}/adresse`);
  },
};

export const productClient = {
  /**
   * Holt die für eine Bewertung verfügbaren Objektarten ab.
   *
   * @returns Promise
   */
  objektarten: () => {
    return axiosClient.get(`${PRODUCT_BASE}/objektarten`);
  },

  /**
   * gets the name with the price of the Kaufpreisanalyse.
   *
   * @returns Promise
   */
  kpaPreis: () => {
    return axiosClient.get(`${PRODUCT_BASE}/kpa-preis`);
  },

  /**
   * Holt die für den Benutzer verfügbaren Produkte ab.
   *
   * @returns Promise
   */
  produktnamen: () => {
    return axiosClient.get(`${PRODUCT_BASE}/produktnamen`);
  },

  /**
   * Holt eine Seite mit Informationen zu den gekauften Unterlagen des aktuellen Benutzers ab.
   *
   * @param page Die Seite.
   * @param pageSize Anzahl der Elemente einer Seite.
   * @param filter Optionaler Filter auf der Abfrage.
   * @returns Promise
   */
  listUnterlagen: (page, pageSize, filter) => {
    return productClient.list('unterlagen', page, pageSize, filter);
  },

  listNdgSaves: (page, pageSize, filter) => {
    return productClient.list('ndg', page, pageSize, filter);
  },

  /**
   * Helper für einen Seitenabruf.
   *
   * @param type 'unterlagen' | 'bewertungen'
   * @param page Die Seite.
   * @param pageSize Anzahl der Elemente einer Seite.
   * @param filter Optionaler Filter auf der Abfrage.
   * @returns Promise
   */
  list: (type, page, pageSize, filter) => {
    let url = `${PRODUCT_BASE}/${type}?page=${page}&pageSize=${pageSize}`;
    if (filter) {
      if (filter.kaufdatumVon) {
        url = url.concat(`&kaufdatumVon=${filter.kaufdatumVon}`);
      }
      if (filter.kaufdatumBis) {
        url = url.concat(`&kaufdatumBis=${filter.kaufdatumBis}`);
      }
      if (filter.plz) {
        url = url.concat(`&plz=${filter.plz}`);
      }
      if (filter.ort) {
        url = url.concat(`&ort=${filter.ort}`);
      }
      if (filter.strasse) {
        url = url.concat(`&strasse=${filter.strasse}`);
      }
      if (filter.hausnummer) {
        url = url.concat(`&hausnummer=${filter.hausnummer}`);
      }
      // Diesen Filter gibt es nur bei den Unterlagen
      if (filter.produkt && type === 'unterlagen') {
        url = url.concat(`&produkt=${filter.produkt}`);
      }
    }
    return axiosClient.get(url);
  },
  productNdg(id, copy = false) {
      return axiosClient.get(`${PRODUCT_BASE}/unterlagen/${id}/ndg${copy ? '?copy=true' : ''}`);
  },
  productKpa(id, copy = false) {
      return axiosClient.get(`${PRODUCT_BASE}/unterlagen/${id}/kpa${copy ? '?copy=true' : ''}`);
  }
};

export const kasseClient = {
  erstelleRndGutachten: ({objektart, objektbesichtigung}) => {
    return axiosClient.post(`${KASSE_BASE}/objektunterlagen/rndGutachten`, {
      name: '',
      value: objektart,
      objektbesichtigung: objektbesichtigung,
      preis: 0,
    });
  },

  erstelleKpaGutachten: () => {
    return axiosClient.post(`${KASSE_BASE}/objektunterlagen/kaufpreisaufteilung`, {}, {headers: {'Content-Type': 'application/json'}});
  },

  uploadOrDelete(file, dtype, attribute, index) {
    if (file) {
      return this.uploadFile(file, dtype, attribute);
    } else {
      return this.deleteFile(dtype, attribute, index);
    }
  },

  uploadFile: (file, dtype, attribute) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return axiosClient.post(`${KASSE_BASE}/objektunterlagen/${dtype}/file/${attribute}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  deleteFile: (dtype, attribute, index) => {
    return axiosClient.delete(`${KASSE_BASE}/objektunterlagen/${dtype}/file/${attribute}/${index}`);
  },

  downloadFile: (dtype, attribut) => {
    return axiosClient.get(`${KASSE_BASE}/objektunterlagen/${dtype}/file/${attribut}`);
  },

  downloadIndexFile: (dtype, attribut, index) => {
    return axiosClient.get(`${KASSE_BASE}/objektunterlagen/${dtype}/file/${attribut}/${index}`);
  },

  vorschaukarte: (data, dtype) => {
    return axiosClient.patch(`${KASSE_BASE}/objektunterlagen/${dtype}/vorschaukarte`, data);
  },

  checkProduct: produkt => {
    return axiosClient.post(`${KASSE_BASE}/objektunterlagen/checkProdukt`, produkt);
  },

  checkOut: warenkorb => {
    return axiosClient.post(`${KASSE_BASE}/objektunterlagen/checkWarenkorb`, warenkorb);
  },
  checkOutRnd: warenkorb => {
    return axiosClient.post(`${KASSE_BASE}/objektunterlagen/checkWarenkorbRnd`, warenkorb);
  },
  checkOutKpa: warenkorb => {
    return axiosClient.post(`${KASSE_BASE}/objektunterlagen/checkWarenkorbKpa`, warenkorb);
  },
  kaufen: () => {
    return axiosClient.post(`${KASSE_BASE}`);
  },
  ladeKasse: () => {
    return axiosClient.get(`${KASSE_BASE}`);
  },
  loescheProdukt: dtype => {
    return axiosClient.delete(`${KASSE_BASE}/objektunterlagen/${dtype}`);
  },
  loescheWarenkorb: () => {
    return axiosClient.delete(`${KASSE_BASE}`);
  },
};
export const metadataClient = {
  ladeAutocompletes: (type, query) => {
    return axiosClient.get(`${META_BASE}/${type}`, {
      params: {query: query},
    });
  },
  ladeTooltip(dtype, tooltip) {
    return axiosClient.get(`${META_BASE}/tooltips/${dtype}`, {
      params: {
        tooltip: tooltip,
      },
    });
  },
  ladeHilfeLink(type) {
    return axiosClient.get(`${META_BASE}/support/${type}`);
  },
};
export const saveClient = {
  speichereKonfiguration(ndgSave) {
    return axiosClient.post(`${SAVE_BASE}/konfiguration/ndg`, ndgSave);
  },
  speichereKonfigurationKpa(ndgSave) {
    return axiosClient.post(`${SAVE_BASE}/konfiguration/kpa`, ndgSave);
  },
  ladeKonfiguration(id) {
    return axiosClient.get(`${SAVE_BASE}/konfiguration/ndg/${id}`);
  },
  ladeKonfigurationKpa(id) {
    return axiosClient.get(`${SAVE_BASE}/konfiguration/kpa/${id}`);
  },
  deleteKonfiguration(id) {
    return axiosClient.delete(`${SAVE_BASE}/konfiguration/${id}`);
  },
  duplicateKonfiguration(id) {
    return axiosClient.post(`${SAVE_BASE}/konfiguration/${id}`);
  },
};
